import { GA_TRACKING_ID } from '../config/constants';
import useAuthStore from '../stores/authStore';

// Check if we're in production environment or if GA is explicitly enabled
const isProduction = import.meta.env.PROD;
const isGAEnabled = isProduction && import.meta.env.VITE_ENABLE_GA === 'true';

// Initialize Google Analytics
export const initializeGA = () => {
  // Only initialize GA in production or when explicitly enabled
  if (!isGAEnabled) {
    console.log('Google Analytics disabled in development mode');
    return;
  }

  // Check if gtag is already loaded
  if (window.gtag) {
    console.log('Google Analytics already initialized');
    return;
  }

  try {
    // Create and append the gtag script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
    document.head.appendChild(script);

    // Initialize dataLayer and gtag function
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;

    // Configure Google Analytics
    gtag('js', new Date());
    
    // Get user info from auth store
    const { user } = useAuthStore.getState();
    configureUserGA(user);

    console.log('Google Analytics initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
  }
};

// Track page views
export const trackPageView = (page_path, page_title) => {
  if (!isGAEnabled || !window.gtag) {
    return;
  }

  try {
    window.gtag('config', GA_TRACKING_ID, {
      page_path,
      page_title,
    });
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
};

// Track custom events
export const trackEvent = (action, customParams = {}) => {
  if (!isGAEnabled || !window.gtag) {
    return;
  }

  try {
    // Get user info from auth store
    const { user } = useAuthStore.getState();
    
    // Add dondon_uid if user is logged in
    if (user && user.id) {
      customParams.dondon_uid = user.id;
    }
    
    window.gtag('event', action, customParams);
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

// Track user interactions
export const trackUserInteraction = (interaction_type, element_name) => {
  trackEvent('user_interaction', interaction_type, element_name);
};

// Track conversions
export const trackConversion = (conversion_type, value) => {
  trackEvent('conversion', conversion_type, null, value);
};

// Configure GA with user information
export const configureUserGA = (user) => {
  if (!isGAEnabled || !window.gtag) {
    return;
  }

  try {
    const config = {
      page_title: document.title,
      page_location: window.location.href,
    };
    
    // Set user_id if user is logged in, otherwise remove it
    if (user && user.id) {
      config.user_id = user.id;
    }
    
    window.gtag('config', GA_TRACKING_ID, config);
  } catch (error) {
    console.error('Failed to configure user GA:', error);
  }
};
